import { Box, Stack, Text } from '@mantine/core';
import { MessageBubble, THREAD_BG, type BubbleBodyProps, type Channel } from './MetaBubbles';
import type { ReceiptView } from './bubbles';

/**
 * Live "what you'll send" preview for the template builders. It renders the
 * in-progress builder value through the SAME native bubble the inbox uses
 * (`MetaBubbles`), so the builder is WYSIWYG and can never drift from the real
 * outbound bubble. Both the Messenger and Instagram template pages feed it their
 * live payload + their channel.
 *
 * Payloads here are the builder-side shapes (camelCase imageUrl/url), not the
 * stored API shapes — best-effort, partial values render gracefully.
 */

interface PreviewButton {
  type?: string;
  title?: string;
  url?: string;
}
interface PreviewElement {
  title?: string;
  subtitle?: string;
  imageUrl?: string;
  buttons?: PreviewButton[];
}
export interface PreviewPayload {
  text?: string;
  quickReplies?: { title?: string }[];
  elements?: PreviewElement[];
  buttons?: PreviewButton[];
  mediaType?: 'image' | 'video';
  url?: string;
  caption?: string;
  mediaId?: string;
  productIds?: string[];
  receipt?: ReceiptView;
}

function btnKind(type?: string): 'url' | 'phone' | 'reply' {
  if (type === 'web_url') return 'url';
  if (type === 'phone_number' || type === 'audio_call') return 'phone';
  return 'reply';
}

/** Map an in-progress builder payload onto native bubble body props. Returns
 *  null when there's nothing meaningful to show yet (renders the empty hint). */
function toBubbleBody(kind: string, p: PreviewPayload): BubbleBodyProps | null {
  switch (kind) {
    case 'quick_replies': {
      const chips = (p.quickReplies ?? []).map((q) => q.title ?? '').filter(Boolean);
      if (!p.text && chips.length === 0) return null;
      return { type: 'quickReplies', text: p.text, quickReplies: chips };
    }
    case 'generic': {
      const cards = (p.elements ?? []).map((el) => ({
        imageUrl: el.imageUrl,
        title: el.title,
        subtitle: el.subtitle,
        buttons: (el.buttons ?? []).map((b) => ({ title: b.title ?? '', kind: btnKind(b.type) })),
      }));
      if (cards.length === 0 || cards.every((c) => !c.title && !c.imageUrl)) return null;
      return { type: 'cards', cards };
    }
    case 'button': {
      if (!p.text && (p.buttons ?? []).length === 0) return null;
      return {
        type: 'buttons',
        text: p.text,
        buttons: (p.buttons ?? []).map((b) => ({ title: b.title ?? '', kind: btnKind(b.type) })),
      };
    }
    case 'media': {
      if (!p.url && !p.caption) return null;
      return {
        type: p.mediaType === 'video' ? 'video' : 'image',
        media: { url: p.url },
        text: p.caption,
      };
    }
    case 'media_share':
      return { type: 'attachment', attachment: { kind: 'Shared a post' } };
    case 'product': {
      const n = p.productIds?.length ?? 0;
      if (n === 0) return null;
      return {
        type: 'attachment',
        attachment: { kind: n > 1 ? `Shared ${n} products` : 'Shared a product' },
      };
    }
    case 'receipt': {
      const r = p.receipt;
      const hasData =
        !!r?.orderNumber ||
        !!r?.recipientName ||
        (r?.elements?.length ?? 0) > 0 ||
        r?.summary?.totalCost != null;
      if (!hasData) return null;
      return {
        type: 'receipt',
        receipt: {
          orderNumber: r?.orderNumber ?? undefined,
          name: r?.recipientName ?? undefined,
          currency: r?.currency ?? undefined,
          items: (r?.elements ?? []).map((e) => ({
            title: e.title ?? 'Item',
            qty: e.quantity ?? undefined,
            price: e.price ?? 0,
          })),
          total: r?.summary?.totalCost ?? undefined,
          paid: r?.paymentMethod ?? undefined,
        },
      };
    }
    default:
      return p.text ? { type: 'text', text: p.text } : null;
  }
}

export function TemplatePreview({
  kind,
  payload,
  channel = 'messenger',
}: {
  kind: string;
  payload: PreviewPayload;
  channel?: Channel;
}) {
  const body = toBubbleBody(kind, payload);
  return (
    <Stack gap={6}>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">
        Preview
      </Text>
      <Box
        style={{
          background: THREAD_BG[channel],
          borderRadius: 10,
          padding: 12,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        {body ? (
          <MessageBubble channel={channel} incoming={false} time="12:30" status="read" {...body} />
        ) : (
          <Text size="xs" c="dimmed" fs="italic">
            Fill in the form to preview the message…
          </Text>
        )}
      </Box>
    </Stack>
  );
}
